// upload.actions.js
import { uploadConstants } from 'store/_constants/upload.constants';
import { uploadServices } from 'store/services/upload.services';
import { snackbarActions } from 'helpers/tools';

/* ---------- UPLOAD TUNING ----------
 * We do NOT hard-code a part size. The backend tells us how many parts exist
 * (one presigned URL per part), so we split the file into exactly that many
 * pieces: partSize = ceil(fileSize / urls.length). This guarantees every URL is
 * used, no part is empty, and the part boundaries always match what the backend
 * expects at "complete" time — no matter the file size.
 *
 * BATCH_SIZE: how many parts are uploaded together per batch. The uploader fires
 * BATCH_SIZE parts at once, waits for ALL of them, then starts the next batch.
 * Set it to 10 for batches of 10, 5 for batches of 5, etc. Browsers cap
 * connections per host at ~6, so a batch bigger than 6 still runs ~6 at a time
 * (the rest queue) — set BATCH_SIZE = 6 if you want the cleanest visual match.
 */
const BATCH_SIZE = 100;
const MAX_RETRIES = 3;

/* ---------- IN-MEMORY UPLOAD MARKER ----------
 * This lives in the JS runtime only. A same-tab SPA remount keeps it; a full
 * page refresh (which kills all in-flight fetches) wipes it. The upload page
 * uses it on mount to decide whether a persisted "in progress" flag is real or
 * stale-after-refresh, so a refresh clears the loader instead of hanging it.
 */
function markUploadInFlight(value) {
  if (typeof window !== 'undefined') {
    window.__uploadInFlight = value;
  }
}

/* ---------- SIMPLE STATE ACTIONS ---------- */
function setPaymentPendingFile(file) {
  return { type: uploadConstants.SET_PAYMENT_PENDING_FILE, file };
}
function clearPaymentPendingFile() {
  return { type: uploadConstants.CLEAR_PAYMENT_PENDING_FILE };
}
function setPaymentProcessActive(active) {
  return { type: uploadConstants.SET_PAYMENT_PROCESS_ACTIVE, active };
}
// Live progress for the upload loader (parts completed out of total).
// Uses a string literal on purpose so it can never be `undefined` even if the
// SET_UPLOAD_PROGRESS constant hasn't been added to the constants file.
function setUploadProgress(completed, total) {
  return { type: 'SET_UPLOAD_PROGRESS', completed, total };
}
function setCancelUploadAvailable(available) {
  return { type: uploadConstants.SET_CANCEL_UPLOAD_AVAILABLE, available };
}
function setPaymentDialogError(message) {
  return { type: uploadConstants.SET_PAYMENT_DIALOG_ERROR, message };
}
function setStripeSessionId(sessionId) {
  return { type: uploadConstants.SET_STRIPE_SESSION_ID, sessionId };
}
function resetUpload() {
  return { type: uploadConstants.RESET_UPLOAD };
}

/* ---------- FETCH HISTORY ---------- */
function getUploadHistory(params) {
  return (dispatch) => {
    dispatch(request());
    uploadServices.getUploadHistory(params).then(
      (res) => {
        const { response, error, message, total, page, params: respParams, currentAvailableLimit } = res;
        if (error) dispatch(failure(true, message));
        else dispatch(success(response, total, page, respParams, currentAvailableLimit));
      },
      (error) => dispatch(failure(true, error?.message || 'Unknown error'))
    );
  };

  function request() {
    return { type: uploadConstants.GET_UPLOAD_HISTORY };
  }
  function success(records, total, page, params, currentAvailableLimit) {
    return { type: uploadConstants.GET_UPLOAD_HISTORY_SUCCESS, records, total, page, params, currentAvailableLimit };
  }
  function failure(error, message) {
    return { type: uploadConstants.GET_UPLOAD_HISTORY_ERROR, error, message };
  }
}

/* ---------- GENERATE URL + DISPATCH UPLOAD ---------- */
function generateUrl(params, file, setFile) {
  return (dispatch, getState) => {
    dispatch(request());
    // Hold the "upload in progress" flag from the very first moment of the
    // upload. It is only cleared when uploadToS3 (the complete call) finishes,
    // so the loader stays up through every part AND the final complete step.
    markUploadInFlight(true);
    dispatch(setPaymentProcessActive(true));

    const state = getState();
    const stripe_session_id = state.uploadReducer.stripe_session_id || null;

    uploadServices.generateUrl(params).then(
      (res) => {
        const { response, error } = res;

        // DIAGNOSTIC: shows exactly what the backend returned.
        // If you do NOT see multiple URLs here, the file is not being split into
        // parts server-side and parallel upload is impossible from the frontend.
        console.log('[upload] RAW generateUrl response:', response);
        console.log(
          '[upload] presigned URLs returned =',
          Array.isArray(response) ? response.length : (response?.signed_urls?.length ?? 'unknown')
        );

        if (error || !response) {
          markUploadInFlight(false);
          dispatch(failure());
          dispatch(setPaymentProcessActive(false));
          dispatch(snackbarActions(true, 'Failed to prepare upload. Please retry.'));
          return;
        }

        // Single-part: backend returns an array of length 1.
        // Multipart:  backend returns { signed_urls: [...], upload_id, object_name }.
        const isSinglePart = Array.isArray(response) && response.length === 1;

        if (isSinglePart) {
          console.log('[upload] single-part upload');
          uploadSingleFile(response[0], file, stripe_session_id, dispatch, success, failure);
        } else {
          const signedUrls = response.signed_urls || (Array.isArray(response) ? response : null);
          console.log('[upload] multipart upload — parts:', signedUrls ? signedUrls.length : 0);
          uploadFileInChunks(
            file,
            signedUrls,
            response.upload_id,
            stripe_session_id,
            response.object_name,
            dispatch,
            () => {}, // onDone
            () => {   // onFail -> never leave the button "loading"
              markUploadInFlight(false);
              dispatch(failure());
              dispatch(setPaymentProcessActive(false));
            }
          );
        }
      },
      (error) => {
        markUploadInFlight(false);
        dispatch(failure());
        dispatch(setPaymentProcessActive(false));
        dispatch(snackbarActions(true, error?.message || 'Upload error'));
      }
    );
  };

  function request() {
    return { type: uploadConstants.GENERATE_URL };
  }
  function success() {
    return { type: uploadConstants.GENERATE_URL_SUCCESS };
  }
  function failure() {
    return { type: uploadConstants.GENERATE_URL_ERROR };
  }
}

/* ---------- SINGLE FILE UPLOAD ---------- */
const uploadSingleFile = async (url, file, stripe_session_id, dispatch, success, failure) => {
  try {
    const response = await fetch(url, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': 'application/zip',
        ...(stripe_session_id && { 'x-stripe-session-id': stripe_session_id })
      }
    });

    if (!response.ok) throw new Error('Single-part upload failed.');

    dispatch(success());
    dispatch(snackbarActions(false, 'File uploaded successfully'));
    dispatch(clearStripeSessionAfterUpload());
    dispatch(uploadActions.getUploadHistory({ page: 0, pageSize: 10 }));
  } catch (error) {
    console.error('Single upload error:', error);
    markUploadInFlight(false);
    if (typeof failure === 'function') dispatch(failure());
    dispatch(setPaymentProcessActive(false));
    dispatch(snackbarActions(true, 'Upload failed. Please retry.'));
  }
};

/* ---------- Upload ONE part with retry + backoff ---------- */
async function uploadChunkWithRetry(url, chunk, partNumber) {
  let lastErr;
  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      console.log(`[upload] -> START part ${partNumber} (attempt ${attempt})`);
      // No custom headers: the presigned URL signs only `host`, so S3 ignores
      // anything else, and extra headers only widen the CORS preflight.
      const response = await fetch(url, {
        method: 'PUT',
        body: chunk
      });

      if (!response.ok) {
        throw new Error(`Part ${partNumber} failed with status ${response.status}`);
      }

      const eTag = response.headers.get('ETag');
      // If ETag is null your S3 CORS is missing  ExposeHeaders: ["ETag"].
      if (!eTag) {
        const visible = [];
        response.headers.forEach((v, k) => visible.push(k));
        console.error(
          `[upload] part ${partNumber}: ETag hidden. Headers the browser can see:`,
          visible
        );
        throw new Error(
          `Part ${partNumber}: the S3 ETag header is not readable. ` +
            `Add "ExposeHeaders": ["ETag"] to the bucket CORS configuration.`
        );
      }

      console.log(`[upload] <- DONE  part ${partNumber}`);
      return { PartNumber: partNumber, ETag: eTag };
    } catch (err) {
      lastErr = err;
      console.warn(`[upload] part ${partNumber} attempt ${attempt} failed:`, err.message);
      if (attempt < MAX_RETRIES) {
        await new Promise((r) => setTimeout(r, 1000 * attempt)); // backoff
      }
    }
  }
  throw lastErr;
}

/* ---------- CHUNKED UPLOAD (STRICT BATCHES) ----------
 * Uploads parts in fixed batches: fire BATCH_SIZE parts together, wait for ALL
 * of them to finish, then start the next batch. Change BATCH_SIZE below to tune.
 * Reminder: browsers cap connections per host at ~6, so within a batch larger
 * than 6 the browser still runs ~6 at a time and queues the rest.
 */
const uploadFileInChunks = async (
  file,
  urls,
  upload_id,
  stripe_session_id,
  object_name,
  dispatch,
  onDone,
  onFail
) => {
  if (!Array.isArray(urls) || urls.length === 0) {
    console.error('[upload] No signed URLs received for multipart upload.');
    if (typeof onFail === 'function') onFail();
    dispatch(snackbarActions(true, 'Upload failed. Please retry.'));
    return;
  }

  const totalChunks = urls.length;

  // Derive the part size from the number of URLs the backend gave us, so the
  // file is split into EXACTLY urls.length non-empty pieces.
  const partSize = Math.ceil(file.size / totalChunks);
  console.log(`[upload] fileSize=${file.size} bytes, parts=${totalChunks}, partSize≈${partSize} bytes, batch=${BATCH_SIZE}`);

  const etags = new Array(totalChunks);
  let completed = 0;

  dispatch(setUploadProgress(0, totalChunks));

  try {
    // Walk the parts in fixed-size batches.
    for (let batchStart = 0; batchStart < totalChunks; batchStart += BATCH_SIZE) {
      const batchEnd = Math.min(batchStart + BATCH_SIZE, totalChunks);
      console.log(`[upload] === BATCH parts ${batchStart + 1}..${batchEnd} — starting all together ===`);

      const batchPromises = [];
      for (let i = batchStart; i < batchEnd; i++) {
        const start = i * partSize;
        const end = Math.min(start + partSize, file.size);
        const partNumber = urls[i].part_number || i + 1; // backend uses "part_number"
        const chunk = file.slice(start, end);

        batchPromises.push(
          uploadChunkWithRetry(urls[i].url, chunk, partNumber).then((result) => {
            etags[i] = result; // index i keeps PartNumber order
            completed += 1;
            dispatch(setUploadProgress(completed, totalChunks)); // advance loader
          })
        );
      }

      // Wait for the ENTIRE batch before moving on to the next one.
      await Promise.all(batchPromises);
      console.log(`[upload] === BATCH parts ${batchStart + 1}..${batchEnd} — done ===`);
    }

    if (etags.some((e) => !e)) {
      throw new Error('One or more parts failed to upload.');
    }

    const payload = { etags, file_name: file.name, upload_id, object_name, stripe_session_id };
    if (typeof onDone === 'function') onDone();
    dispatch(uploadToS3(payload));
  } catch (error) {
    console.error('[upload] Chunk upload error:', error);
    if (typeof onFail === 'function') onFail();
    dispatch(snackbarActions(true, error?.message || 'Upload failed. Please retry.'));
  }
};

/* ---------- COMPLETE S3 UPLOAD ---------- */
function uploadToS3(payload) {
  return (dispatch) => {
    dispatch(request());
    uploadServices.uploadToS3(payload).then(
      (res) => {
        const { error } = res;
        if (error) {
          markUploadInFlight(false);
          dispatch(failure());
          dispatch(setPaymentProcessActive(false));
          dispatch(snackbarActions(true, 'File upload failed. Please try again!'));
        } else {
          dispatch(success());
          dispatch(uploadActions.clearStripeSessionAfterUpload());
          dispatch(uploadActions.getUploadHistory({ page: 0, pageSize: 10 }));
          dispatch(snackbarActions(false, 'File uploaded successfully'));
        }
      },
      (error) => {
        markUploadInFlight(false);
        dispatch(failure());
        dispatch(setPaymentProcessActive(false));
        dispatch(snackbarActions(true, error?.message || 'Upload to S3 failed'));
      }
    );
  };

  function request() {
    return { type: uploadConstants.UPLOAD_TO_S3 };
  }
  function success() {
    return { type: uploadConstants.UPLOAD_TO_S3_SUCCESS };
  }
  function failure() {
    return { type: uploadConstants.UPLOAD_TO_S3_ERROR };
  }
}

/* ---------- DELETE HISTORY ---------- */
function deleteHistory(params) {
  return (dispatch) => {
    dispatch(request());
    uploadServices.deleteHistory(params).then(
      (res) => {
        const { response, error, message } = res;
        if (error) {
          dispatch(failure(true, message));
          dispatch(snackbarActions(true, message));
        } else {
          dispatch(success(response, message));
          dispatch(snackbarActions(false, message));
          dispatch(uploadActions.getUploadHistory({ page: 0, pageSize: 10 }));
        }
      },
      (error) => dispatch(failure(true, error.message || 'Delete failed'))
    );
  };

  function request() {
    return { type: uploadConstants.DELETE_HISTORY };
  }
  function success(records, message) {
    return { type: uploadConstants.DELETE_HISTORY_SUCCESS, records, message };
  }
  function failure(error, message) {
    return { type: uploadConstants.DELETE_HISTORY_ERROR, error, message };
  }
}

/* ---------- STRIPE PAYMENT INTENT ---------- */
function getStripePaymentIntent(payload) {
  return (dispatch, getState) => {
    dispatch(setPaymentDialogError(null));
    dispatch(request());

    uploadServices.getStripePaymentIntent(payload).then(
      (res) => {
        const { data, error, message } = res;
        if (error) {
          const msg = message || 'Payment error';
          dispatch(failure(true, msg));
          dispatch(setPaymentDialogError(msg));
          dispatch(snackbarActions(true, msg));
          return;
        }

        dispatch(success(data, message));

        const paymentType = data?.payment_type;
        const paymentStatus = data?.payment_status?.toLowerCase();
        const stripe_session_id = data?.stripe_session_id;

        if (paymentType === 'wallet' && paymentStatus === 'paid') {
          const state = getState();
          const pendingFile = state.uploadReducer.pendingFile;

          if (pendingFile) {
            // Do NOT set paymentProcessActive here — the upload hasn't started
            // yet, we're only showing the approval countdown. The flag is set
            // when generateUrl actually begins uploading.
            setTimeout(() => dispatch(setCancelUploadAvailable(true)), 3000);

            dispatch({
              type: uploadConstants.SET_WALLET_PAYMENT_APPROVAL_NEEDED,
              payload: {
                needsApproval: true,
                file: pendingFile,
                stripe_session_id: stripe_session_id
              }
            });
          } else {
            dispatch(snackbarActions(true, 'No pending file found. Please reselect and upload again.'));
          }
          return; // wallet doesn't go to Stripe
        }

        if (data?.stripe_session_id) {
          dispatch(setStripeSessionId(data.stripe_session_id));
        }
      },
      (error) => {
        const errMsg = error?.message || 'Payment error';
        dispatch(failure(true, errMsg));
        dispatch(setPaymentDialogError(errMsg));
        dispatch(snackbarActions(true, errMsg));
      }
    );
  };

  function request() {
    return { type: uploadConstants.GET_STRIPE_PAYMENT_LINK };
  }
  function success(stripeDetails, message) {
    return { type: uploadConstants.GET_STRIPE_PAYMENT_LINK_SUCCESS, stripeDetails, message };
  }
  function failure(error, message) {
    return { type: uploadConstants.GET_STRIPE_PAYMENT_LINK_ERROR, error, message };
  }
}

/* ---------- WALLET APPROVAL ---------- */
function approveWalletUpload() {
  return (dispatch, getState) => {
    const state = getState();
    const { walletApprovalData } = state.uploadReducer;

    if (walletApprovalData?.needsApproval && walletApprovalData.file) {
      dispatch({ type: uploadConstants.APPROVE_WALLET_UPLOAD });

      dispatch(
        uploadActions.generateUrl(
          {
            size: walletApprovalData.file.size,
            name: walletApprovalData.file.name,
            stripe_session_id: walletApprovalData.stripe_session_id
          },
          walletApprovalData.file,
          () => {}
        )
      );
    }
  };
}

/* ---------- STRIPE PAYMENT STATUS ---------- */
function getStripePaymentStatus(payload) {
  return (dispatch, getState) => {
    dispatch(uploadActions.setPaymentDialogError(null));
    dispatch(request());

    uploadServices.getStripePaymentStatus(payload).then(
      (res) => {
        const { error, success: apiSuccess, message, data } = res;
        if (error || !apiSuccess) {
          const msg = message || 'Payment verification failed. Please retry.';
          dispatch(failure(true, msg));
          dispatch(uploadActions.setPaymentDialogError(msg));
          return;
        }

        const paymentStatus = data?.payment_status || null;
        const sessionId = payload?.stripe_session_id || null;
        if (sessionId) dispatch(setStripeSessionId(sessionId));

        dispatch(success(paymentStatus));

        const normalized = (paymentStatus || '').toLowerCase();
        const successStates = ['completed', 'succeeded', 'paid', 'success'];

        if (successStates.includes(normalized)) {
          const state = getState();
          const pendingFile = state.uploadReducer.pendingFile;
          if (pendingFile) {
            dispatch(setPaymentProcessActive(true));
            dispatch(
              uploadActions.generateUrl(
                { size: pendingFile.size, name: pendingFile.name, stripe_session_id: sessionId },
                pendingFile,
                () => {}
              )
            );
          } else {
            dispatch(snackbarActions(true, 'Payment completed. Please re-select the file to upload.'));
          }
        } else {
          dispatch(uploadActions.setPaymentDialogError(`Payment is in status "${paymentStatus}". Please retry later.`));
        }
      },
      (error) => {
        const errMsg = error?.message || 'Payment verification failed. Please retry.';
        dispatch(failure(true, errMsg));
        dispatch(uploadActions.setPaymentDialogError(errMsg));
      }
    );
  };

  function request() {
    return { type: uploadConstants.GET_STRIPE_PAYMENT_STATUS };
  }
  function success(payment_status) {
    return { type: uploadConstants.GET_STRIPE_PAYMENT_STATUS_SUCCESS, payment_status };
  }
  function failure(error, message) {
    return { type: uploadConstants.GET_STRIPE_PAYMENT_STATUS_ERROR, error, message };
  }
}

/* ---------- WALLET BALANCE ---------- */
function getWalletBalance(params) {
  return (dispatch) => {
    dispatch(request());
    uploadServices.getWalletBalance(params).then(
      (res) => {
        const { error, success: apiSuccess, message, data } = res;
        if (error || !apiSuccess) {
          const msg = message || 'Failed to get wallet balance.';
          dispatch(failure(true, msg));
        } else {
          const wallet_balance = data.wallet_balance;
          dispatch(success(wallet_balance));
        }
      },
      (error) => {
        const errMsg = error?.message || 'Failed to get wallet balance.';
        dispatch(failure(true, errMsg));
      }
    );
  };

  function request() {
    return { type: uploadConstants.GET_WALLET_BALANCE };
  }
  function success(wallet_balance) {
    return { type: uploadConstants.GET_WALLET_BALANCE_SUCCESS, wallet_balance };
  }
  function failure(error, message) {
    return { type: uploadConstants.GET_WALLET_BALANCE_ERROR, error, message };
  }
}

/* ---------- CLEAR STRIPE SESSION ---------- */
function clearStripeSessionAfterUpload() {
  return (dispatch) => {
    markUploadInFlight(false);
    dispatch({ type: uploadConstants.SET_STRIPE_SESSION_ID, sessionId: null });
    dispatch(clearPaymentPendingFile());
    dispatch(setPaymentProcessActive(false));
    dispatch(setCancelUploadAvailable(false));
  };
}

/* ---------- EXPORT ---------- */
export const uploadActions = {
  getUploadHistory,
  generateUrl,
  uploadToS3,
  deleteHistory,
  getStripePaymentIntent,
  getStripePaymentStatus,
  setPaymentPendingFile,
  clearPaymentPendingFile,
  setPaymentProcessActive,
  setUploadProgress,
  setCancelUploadAvailable,
  setPaymentDialogError,
  setStripeSessionId,
  clearStripeSessionAfterUpload,
  getWalletBalance,
  approveWalletUpload,
  resetUpload,
};